import {Button, Card, CardContent, Grid, useTheme} from "@mui/material";
import React, {MouseEventHandler, useContext, useEffect, useState,} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {unitsEnumAtom} from "../../../state/rentalPeriodsRecoil";
import {ancillaryTypeAtom, chargingWayAtom, currencyAtom,} from "../../../state/constantsRecoil";
import CustomBanner from "../../../common/components/CustomBanner";
import {useFormContext} from "react-hook-form";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {SelectElementModelFE} from "../../../api/types/generic";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {FormOWAncillaryModel, OwAncillaryPrePopulatedFields,} from "../../../api/types/owAncillary";
import {useOWAncillaryRepository} from "../../../api/repositories/OWAncillaryRepository";
import {setFormNumberValue} from "../../../utils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";
import {owAncillaryAtom} from "../../../state/owAncillariesRecoil";
import {numberIntegerRegex} from "../../../constants";
import {useAncillaryRepository} from "../../../api/repositories/AncillaryRepository";
import DealerAndTenant from "../../../common/components/DealerAndTenant";

const OWAncillaryBaseDefinition = ({
                                       readOnly,
                                       handleSubmit,
                                       providers,
                                       ancillaryInUse,
                                   }: {
    readOnly: boolean;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
    providers: SelectElementModelFE[];
    ancillaryInUse: boolean;
}) => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["tranlsation", "pricing"]);


    const {getOwAncillaryPrePopulatedFields} = useOWAncillaryRepository();

    //Use State
    const [serviceItem, setServiceItem] = useState<any>([]);

    // Use Recoil
    const [commericalSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [unitsEnum] = useRecoilState(unitsEnumAtom);
    const [currencyList] = useRecoilState(currencyAtom);
    const [chargingWayList] = useRecoilState(chargingWayAtom);
    const [types] = useRecoilState(ancillaryTypeAtom);
    const [owAncillary] = useRecoilState(owAncillaryAtom);

    // Use Repository Hooks
    const {getServiceItem} = useAncillaryRepository();

    // Use Form Context
    const {register, setValue, watch, formState} =
        useFormContext<FormOWAncillaryModel>();
    const form = useFormContext<FormOWAncillaryModel>();
    const watchAllFields = watch();

    const getStaticContents = async () => {
        const fields: OwAncillaryPrePopulatedFields =
            await getOwAncillaryPrePopulatedFields();
        setValue("type", fields.type);
        setValue("subtype", fields.subtype);
        setValue("cardinality", fields.cardinality);
        setValue("tpUnit", fields.tpUnit);
        setValue("chargeType", fields.chargeType.toUpperCase());
        setValue("chargingWay", fields.chargingWay.toUpperCase());

        // @ts-ignore
        const services = await getServiceItem(form.getValues("legalEntity")?.code, form.getValues("dealer")?.code);
        setServiceItem(services);
    };

    useEffect(() => {
        if (form.getValues("legalEntity") && form.getValues("dealer")) {
            getStaticContents();
        }
    }, [form.getValues("legalEntity"), form.getValues("dealer")]);

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && ancillaryInUse && (
                <CustomBanner
                    message={t("pricing:ancillaryOW.banner")}
                    severity={"warning"}
                />
            )}
            <Grid item xs={12} sm={12} md={12}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} py={2}>
                            <DealerAndTenant
                                readOnly={readOnly}
                                md={3}
                                legalEntityName={"legalEntity"}
                                dealerName={"dealer"}
                                dealerIsVisible={true}
                                idForReadOnly={form.getValues("id")}
                            />
                            {/* COMMERCIAL SEGMENT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("segments")}
                                    label={`${t(
                                        "pricing:ancillary.form.labels.commercialSegment"
                                    )}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={commericalSegmentList || []}
                                    multiple={true}
                                    isOptionEqualToValue={(
                                        option: SelectElementModelFE,
                                        value: SelectElementModelFE
                                    ) => option?.code === value?.code}
                                />
                            </Grid>

                            {/* CODE */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("code")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.code")}*`}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* NAME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("nameAncillary")}
                                    label={`${t("pricing:ancillary.form.labels.nameAncillary")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* DESCRIPTION */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("description")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.description"
                                    )}*`}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("type")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.type")}`}
                                    options={types}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                    readOnly={true}
                                />
                            </Grid>
                            {/* SUBTYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("subtype")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.subtype"
                                    )}`}
                                    options={[]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                    readOnly={true}
                                />
                            </Grid>
                            {/* APPLICATION TIMING */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("applicationTiming")}
                                    label={t(
                                        "pricing:ancillaryLevelOne.form.labels.applicationTiming"
                                    )}
                                    readOnly={readOnly}
                                    options={["PICK UP", "DROP OFF"]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                />
                            </Grid>
                            {/* TOLERANCE TIME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("toleranceTime")}
                                    label={t(
                                        "pricing:ancillaryLevelOne.form.labels.toleranceTime"
                                    )}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "toleranceTime",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* CARDINALITY */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("cardinality")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.cardinality"
                                    )}`}
                                    readOnly={true}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "cardinality",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* ADDITIONAL FIELD */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("additionalField")}
                                    label={t(
                                        "pricing:ancillaryLevelOne.form.labels.additionalFieldType"
                                    )}
                                    readOnly={readOnly}
                                    options={["NUMBER", "STRING"]}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                />
                            </Grid>
                            {/* UNIT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("tpUnit")}
                                    label={`${t("pricing:ancillary.form.labels.unit")}`}
                                    readOnly={true}
                                    options={unitsEnum || []}
                                    isOptionEqualToValue={(option: string, value: string) =>
                                        option === value
                                    }
                                />
                            </Grid>
                            {/* TAX */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("tax")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.tax")}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* RESERVATION FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationFrom")}
                                    label={`${t(
                                        "pricing:ancillary.form.labels.reservationFrom"
                                    )}*`}
                                    readOnly={
                                        readOnly || formState.isSubmitting || ancillaryInUse
                                    }
                                    minDate={
                                        dayjs().diff(
                                            dayjs(owAncillary?.prcAncillary?.reservationFrom),
                                            "minutes"
                                        ) > 0
                                            ? owAncillary?.prcAncillary?.reservationFrom
                                            : undefined
                                    }
                                />
                            </Grid>
                            {/* RESERVATION TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationTo")}
                                    label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.reservationFrom || undefined}
                                />
                            </Grid>
                            {/* PICKUP DATE FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupFrom")}
                                    label={`${t(
                                        "pricing:ancillary.form.labels.pickupDateFrom"
                                    )}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={
                                        dayjs().diff(
                                            dayjs(owAncillary?.prcAncillary?.pickupFrom),
                                            "minutes"
                                        ) > 0
                                            ? owAncillary?.prcAncillary?.pickupFrom
                                            : undefined
                                    }
                                />
                            </Grid>
                            {/* PICKUP DATE TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupTo")}
                                    label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.pickupFrom || undefined}
                                />
                            </Grid>
                            {/* CURRENCY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("currency")}
                                    label={`${t("pricing:ancillary.form.labels.currency")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={currencyList}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            {/* TENANT */}
                            {/*<Grid item xs={ 12 } md={ 3 }>*/}
                            {/*    <FormAutocomplete*/}
                            {/*        { ...register("dealer") }*/}
                            {/*        label={ `${ t("pricing:ancillary.form.labels.tenant") }*` }*/}
                            {/*        readOnly={ readOnly || formState.isSubmitting }*/}
                            {/*        options={ providers }*/}
                            {/*        isOptionEqualToValue={ (option, value) => option.code === value.code }*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            {/* MINIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("minimumAmount")}
                                    label={`${t("pricing:ancillary.form.labels.minimumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "minimumAmount",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* MAXIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("maximumAmount")}
                                    label={`${t("pricing:ancillary.form.labels.maximumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "maximumAmount",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* CHARGING WAY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargingWay")}
                                    label={`${t("pricing:ancillary.form.labels.chargingWay")}`}
                                    readOnly={true}
                                    options={chargingWayList}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            {/* MAX UNIT*/}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("maxUnits")}
                                    label={`${t("pricing:ancillary.form.labels.maxUnits")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) =>
                                        setFormNumberValue(
                                            value,
                                            setValue,
                                            "maxUnits",
                                            numberIntegerRegex
                                        )
                                    }
                                />
                            </Grid>
                            {/* CHARGE TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargeType")}
                                    label={`${t("pricing:ancillary.form.labels.chargeType")}`}
                                    readOnly={true}
                                    options={["VALUE", "PERCENTAGE"]}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            {/* SELL DESK */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("sellDesk")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.sellDesk")}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* TAXCODE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("taxCode")}
                                    label={`${t(
                                        "pricing:ancillaryLevelOne.form.labels.taxCode"
                                    )}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={serviceItem.map((item : any) => ({
                                        label: item.name,
                                        value: item.code,
                                    }))}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            </Grid>
                            {/* NOTE */}
                            <Grid item xs={12} md={12}>
                                <FormTextField
                                    {...register("note")}
                                    label={`${t("pricing:ancillary.form.labels.notes")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {!readOnly && (
                <Grid
                    container
                    item
                    xs={12}
                    justifyContent="flex-end"
                    columnGap={2}
                >
                    <Grid>
                        <Button
                            variant="outlined"
                            sx={{marginRight: "15px"}}
                            color={"accent"}
                            type={"button"}
                            disabled={formState.isSubmitting}
                            onClick={() => window.close()}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button
                            color={"accent"}
                            type={"button"}
                            onClick={handleSubmit}
                            sx={{border: `1px solid ${theme.palette.accent.main}`}}
                            disabled={formState.isSubmitting}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default OWAncillaryBaseDefinition;
